import { useCallback, useEffect, useMemo, useState } from 'react';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import { useFeatureTheme } from '../../../../theme/hooks/useFeatureTheme';
import { useSnackBar } from '../../../../components/FlowBuilder/Common/SnackBar/index.tsx';
import { templateService } from '../../services/templateService';
import { MAX_TEMPLATE_TAGS } from '../../utils/templateConstants';
import useTemplateSx from '../../hooks/useTemplateSx';

const filter = createFilterOptions();

function mapTagOption(tag) {
  if (!tag) return null;
  return {
    id: tag.id,
    name: tag.name,
    label: tag.name,
    value: tag.id,
  };
}

export default function TemplateTagsSelect({
  companyId,
  value = [],
  onChange,
  label = 'Tags',
  placeholder = 'Type to search or create tags',
  disabled = false,
  error = false,
  helperText,
  sx,
}) {
  const styles = useTemplateSx();
  const { showSnackBar } = useSnackBar();
  const { semantic, radiusPx, typography } = useFeatureTheme();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const selected = useMemo(
    () => (Array.isArray(value) ? value.filter(Boolean) : []),
    [value]
  );

  const atMax = selected.length >= MAX_TEMPLATE_TAGS;

  const loadTags = useCallback(async () => {
    if (!companyId) {
      setOptions([]);
      return;
    }
    setLoading(true);
    try {
      const res = await templateService.getTemplateTags(companyId);
      const list = res?.data?.data?.results ?? res?.data?.results ?? [];
      setOptions(list.map(mapTagOption).filter(Boolean));
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    loadTags();
  }, [loadTags]);

  const createTagByName = useCallback(
    async (name) => {
      const trimmed = name?.trim();
      if (!trimmed || !companyId || atMax) return false;

      const exists = [...options, ...selected].some(
        (option) => option.name?.toLowerCase() === trimmed.toLowerCase()
      );
      if (exists) {
        showSnackBar('Tag already exists', 'warning');
        return false;
      }

      setCreating(true);
      try {
        const res = await templateService.createTemplateTag(trimmed, companyId);
        const created = res?.data?.data ?? res?.data;
        if (created?.id) {
          const option = mapTagOption(created);
          setOptions((prev) => {
            if (prev.some((item) => item.id === option.id)) return prev;
            return [...prev, option].sort((a, b) => a.name.localeCompare(b.name));
          });
          onChange?.([...selected, option].slice(0, MAX_TEMPLATE_TAGS));
          setInputValue('');
          return true;
        }
        showSnackBar(res?.data?.data || 'Failed to create tag', 'error');
      } catch (err) {
        showSnackBar(err?.message || 'Failed to create tag', 'error');
      } finally {
        setCreating(false);
      }
      return false;
    },
    [atMax, companyId, onChange, options, selected, showSnackBar]
  );

  const filterOptions = (opts, params) => {
    const filtered = filter(opts, params);
    const input = params.inputValue.trim();

    if (input !== '' && !atMax) {
      const exists = opts.some(
        (option) => option.name?.toLowerCase() === input.toLowerCase()
      );
      if (!exists) {
        filtered.unshift({
          inputValue: input,
          name: `Create "${input}"`,
          isCreateOption: true,
        });
      }
    } else if (!input && !atMax && opts.length === 0) {
      filtered.push({
        id: '__create_hint__',
        name: 'Type a tag name above, then pick Create or press Enter',
        isHint: true,
      });
    }

    return filtered;
  };

  const handleChange = async (_event, newValue) => {
    if (!onChange) return;

    const last = newValue[newValue.length - 1];
    if (last?.isCreateOption) {
      await createTagByName(last.inputValue);
      return;
    }

    if (last?.isHint) return;

    onChange(newValue.filter((item) => !item?.isHint).slice(0, MAX_TEMPLATE_TAGS));
  };

  const handleInputKeyDown = (event) => {
    if (event.key !== 'Enter' || !inputValue.trim() || atMax) return;
    event.preventDefault();
    event.stopPropagation();
    createTagByName(inputValue);
  };

  const themedSx = {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      borderRadius: radiusPx.md,
      backgroundColor: semantic.inputs.background,
      fontSize: typography.fontSizeMd,
      '& fieldset': { borderColor: semantic.borders.border },
      '&:hover fieldset': { borderColor: semantic.borders.borderStrong },
      '&.Mui-focused fieldset': { borderColor: semantic.interactive.primary },
    },
    ...styles.formSelectControl,
  };

  return (
    <Box sx={{ width: '100%', ...sx }}>
      <Autocomplete
        multiple
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        fullWidth
        size="small"
        disabled={disabled || !companyId || creating}
        options={options}
        value={selected}
        inputValue={inputValue}
        onInputChange={(_event, newInputValue, reason) => {
          if (reason !== 'reset') {
            setInputValue(newInputValue);
          }
        }}
        loading={loading || creating}
        filterOptions={filterOptions}
        getOptionLabel={(option) => {
          if (typeof option === 'string') return option;
          if (option.isCreateOption || option.isHint) return option.name;
          return option.name || option.label || '';
        }}
        isOptionEqualToValue={(option, val) => {
          if (option?.isHint || val?.isHint) return false;
          if (option?.isCreateOption || val?.isCreateOption) return false;
          return option.id === val.id;
        }}
        getOptionDisabled={(option) => option.isHint}
        onChange={handleChange}
        noOptionsText={
          companyId
            ? 'Type a tag name to create one'
            : 'Select organisation first'
        }
        sx={themedSx}
        renderOption={(props, option) => (
          <li
            {...props}
            key={option.id || option.inputValue || option.name}
            style={{
              ...(option.isCreateOption
                ? { fontWeight: 600, color: semantic.interactive.primary }
                : {}),
              ...(option.isHint ? { fontStyle: 'italic', opacity: 0.75 } : {}),
            }}
          >
            {option.isCreateOption ? `+ ${option.name}` : option.name}
          </li>
        )}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.id || index}
              label={option.name || option.label}
              size="small"
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={
              atMax ? `Maximum ${MAX_TEMPLATE_TAGS} tags` : placeholder
            }
            error={error}
            helperText={
              helperText ||
              (companyId
                ? `Up to ${MAX_TEMPLATE_TAGS} tags. Type a name, then select Create or press Enter.`
                : 'Select organisation to manage tags')
            }
            onKeyDown={handleInputKeyDown}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading || creating ? (
                    <CircularProgress color="inherit" size={18} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Box>
  );
}
